<template>
  <permission-gate moduleKey="especificacion-tecnica">
    <main-breadcrumb
      :items="items"
      showBackButton
    ></main-breadcrumb>
    <main-buttons-bar :align="'space-between'">
      <template #left>
        <custom-title>Listado de especificaciones técnicas</custom-title>
      </template>
      <template #right>
        <template v-if="!canCreateET">
          <el-tooltip
            content="El usuario no posee permisos de edición"
            placement="top"
          >
            <el-button
              type="primary"
              size="default"
              :icon="IconAdd"
              disabled
            >
              Nueva especificación técnica
            </el-button>
          </el-tooltip>
        </template>
        <template v-else>
          <el-button
            type="primary"
            size="default"
            :icon="IconAdd"
            @click="() => (modalVisible = true)"
          >
            Nueva especificación técnica
          </el-button>
          <modal-select-product-type
            v-model="modalVisible"
            title="Nueva especificación técnica"
            url="/page/technical-specifications/edit"
            @close-modal="() => (modalVisible = false)"
          />
        </template>

        <template v-if="!creatingRequest">
          <el-tooltip
            v-if="!canCreateRequest"
            content="El usuario no posee permisos de creación de solicitudes"
            placement="top"
          >
            <el-button
              type="primary"
              size="default"
              style="margin-left: 1rem"
              :icon="IconAdd"
              disabled
            >
              Crear solicitud
            </el-button>
          </el-tooltip>

          <el-button
            v-else
            type="primary"
            size="default"
            style="margin-left: 1rem"
            :icon="IconAdd"
            :disabled="!enabledCreateRequest"
            @click="creatingRequest = true"
          >
            Crear solicitud
          </el-button>
        </template>

        <template v-else>
          <el-row>
            <el-button
              type="primary"
              size="default"
              style="margin-left: 1rem"
              :icon="IconCheck"
              @click="confirmRequestCreation"
            >
              Confirmar creación
            </el-button>
            <el-button
              type="default"
              size="default"
              style="margin-left: 1rem"
              :icon="IconCancel"
              @click="creatingRequest = false"
            >
              Cancelar
            </el-button>
          </el-row>
        </template>
      </template>
    </main-buttons-bar>
    <technical-specification-filters :creatingRequest="creatingRequest" />
    <custom-table
      :requestApi="findAllEspecificacionesTecnicasHTTP"
      :requestArgs="filters"
      :pathEdit="'/page/technical-specifications/edit'"
      :paramId="'Codigo'"
      :data="especificacionesTecnicas"
      :columns="columns"
      :showDetailButton="false"
      :firstLoad="false"
      :fixedActions="'right'"
      :selecting="creatingRequest"
      @selection-change="handleSelectionChange"
    >
      <template #version-slot="{ row }">
        <div
          class="version-pill"
          :class="getBadgeClass(row.CicloVida)"
        >
          <div class="version-circle">V{{ row.Version }}</div>
          <span class="version-text">{{ row.CicloVida }}</span>
        </div>
      </template>
      <template #custom-actions="{ row }">
        <el-tooltip
          :content="
            !canCreateWorkOrder
              ? 'El usuario no posee permisos de creación de órdenes de producción'
              : getOpTooltip(row)
          "
          placement="bottom"
        >
          <span>
            <el-button
              :icon="IconCreate"
              size="small"
              :disabled="!canCreateWorkOrder || !isApprovedRow(row)"
              @click.prevent="handleGenerateOPFromList(row)"
            />
          </span>
        </el-tooltip>
        <el-tooltip
          :content="!canCreateET ? 'El usuario no posee permisos de creación' : 'Duplicar ET'"
          placement="bottom"
        >
          <span>
            <el-button
              :icon="IconCopy"
              size="small"
              :disabled="!canCreateET"
              style="margin-right: 8px"
              @click.prevent="handleOpenDuplicateModal(row)"
            />
          </span>
        </el-tooltip>
      </template>
    </custom-table>

    <modal-generate-op-from-et
      v-model="modalGenOpVisible"
      :codigoET="genOpCodigoET"
      :codigoElementoTipo="genOpCodigoElementoTipo"
      :initialCodigoUnidadVenta="genOpCodigoUnidadVenta"
      @confirm="confirmGenerateOp"
    />
    <modal-duplicate-technical-specification
      v-model="modalDuplicateVisible"
      :codigoET="duplicateCodigoET"
      @confirm="confirmDuplicate"
    />
  </permission-gate>
</template>

<script setup>
import {
  ref,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useTechnicalSpecificationStore } from '../stores/technical_specification_store.js';
import { useWorkOrderStore } from '@/views/work_orders/stores/work_order_store.js';
import { useElementoTipoStore } from '@/stores/elemento_tipo/elemento_tipo_store.js';
import { useRequestStore } from '@/views/requests/stores/request_store.js';
import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import CustomTable from '@/shared/components/custom_table.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import IconAdd from '~icons/mdi/add';
import IconCancel from '~icons/mdi/cancel';
import IconCheck from '~icons/mdi/check';
import IconCreate from '~icons/mdi/folder-plus-outline';
import IconCopy from '~icons/mdi/content-copy';
import ModalSelectProductType from '@/shared/components/modal_select_product_type.vue';
import ModalGenerateOpFromEt from '@/shared/components/modal_generate_op_from_et.vue';
import TechnicalSpecificationFilters from './technical_specification_filters.vue';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useLoading } from '@/shared/composables/loading.js';
import { useRouter } from 'vue-router';
import { EstadoSolicitud } from '@/enum/estado_solicitud';
import PermissionGate from '@/shared/components/permission_gate.vue';
import ModalDuplicateTechnicalSpecification from '@/shared/components/modal_duplicate_technical_specification.vue';
import { useAuthStore } from '@/views/auth/auth_store';
import { canCreateModule } from '@/shared/permissions/permission.utils.js';

const router = useRouter();
const usuarioActual = useAuthStore().usuario();
const canCreateET = canCreateModule(usuarioActual, 'especificacion-tecnica');
const canCreateRequest = canCreateModule(usuarioActual, 'solicitud');
const canCreateWorkOrder = canCreateModule(usuarioActual, 'orden-produccion');
const elementoTipoStore = useElementoTipoStore();
const technicalSpecificationStore = useTechnicalSpecificationStore();
const requestStore = useRequestStore();
const workOrderStore = useWorkOrderStore();
const { especificacionesTecnicas, enabledCreateRequest, filters } = storeToRefs(
  technicalSpecificationStore,
);
const {
  firstEspecificacion,
  resetFilter,
  emptyList,
  cloneEspecificacionTecnicaHTTP,
  findAllEspecificacionesTecnicasHTTP,
} = technicalSpecificationStore;
const { createFromETHTTP } = workOrderStore;

const modalVisible = ref(false);
const creatingRequest = ref(false);
const selectedRows = ref([]);
const genOpCodigoElementoTipo = ref(null);
const modalDuplicateVisible = ref(false);
const duplicateCodigoET = ref(null);
// local state
const items = [
  { label: 'Inicio', to: '/page/home', replace: false },
  { label: 'Listado', to: null, replace: false },
];

const columns = [
  { field: 'Codigo', label: 'ID', width: 100, sortable: true, isVisible: false },
  { field: 'CodigoBase', label: 'Nro ET', width: 100, sortable: true, isVisible: true },
  {
    field: 'FechaCreacion',
    label: 'Fecha solicitud',
    width: 120,
    sortable: true,
    isVisible: true,
    isDate: true,
  },
  { field: 'Cliente', label: 'Cliente', width: 280, sortable: true, isVisible: true },
  { field: 'Producto', label: 'Producto', width: 250, sortable: true, isVisible: true },
  { field: 'UnidadVenta', label: 'Unidad Venta', width: 130, sortable: true, isVisible: true },
  {
    field: 'CodigosEquivalentes',
    label: 'Código Equivalente',
    width: 180,
    sortable: true,
    isVisible: true,
  },
  {
    field: 'CodigosAdicionales',
    label: 'Código adicional',
    width: 180,
    sortable: true,
    isVisible: true,
  },
  {
    field: 'CicloVida',
    label: 'Versión',
    width: 140,
    sortable: false,
    isVisible: true,
    slot: 'version-slot',
  },
  { field: 'Estado', label: 'Estado', width: 120, sortable: true, isVisible: true },
  {
    field: 'FechaEstado',
    label: 'Fecha estado',
    width: 120,
    sortable: true,
    isVisible: true,
    isDate: true,
  },
];

const modalGenOpVisible = ref(false);
const genOpCodigoET = ref(null);
const genOpCodigoUnidadVenta = ref(null);

const isApprovedRow = (row) => {
  if (row?.CodigoEstado !== null) return row.CodigoEstado === EstadoSolicitud.ACEPTADA;

  const estadoTxt = (row?.Estado ?? '').toString().trim().toLowerCase();
  return estadoTxt === 'aceptada';
};

const getOpTooltip = (row) => {
  return isApprovedRow(row) ? 'Generar OP' : 'La ET debe estar aprobada para Generar OP';
};

const handleOpenDuplicateModal = (row) => {
  if (!row?.Codigo) return;
  duplicateCodigoET.value = row.Codigo;
  modalDuplicateVisible.value = true;
};

const getBadgeClass = (cicloVida) => {
  const map = {
    Vigente: 'badge-success',
    Borrador: 'badge-warning',
    Obsoleta: 'badge-danger',
    Anulada: 'badge-info',
  };
  return map[cicloVida] || 'badge-info';
};

const confirmDuplicate = async ({ codigoET }) => {
  if (!canCreateET) return;
  try {
    useLoading().openLoading();

    const etDuplicada = await cloneEspecificacionTecnicaHTTP(codigoET);

    if (!etDuplicada?.Codigo) throw new Error('No se devolvió el código de la nueva ET');

    modalDuplicateVisible.value = false;
    useMessageSuccess().open(`¡Especificación Técnica duplicada exitosamente!`);

    router.push(`/page/technical-specifications/edit/${etDuplicada.Codigo}`);
  } catch (e) {
    console.error(e);
    useMessageError().open('Ha ocurrido un error al intentar duplicar la Especificación Técnica.');
  } finally {
    useLoading().closeLoading();
  }
};

const confirmRequestCreation = async () => {
  if (!canCreateRequest) return;
  const especificacionesTecnicas = selectedRows.value.map((row) => row.Codigo);

  if (especificacionesTecnicas.length === 0) {
    useMessageError().open(`No se han seleccionado ninguna especificación técnica.`);
    return;
  }

  try {
    useLoading().openLoading();
    const codigoSolicitud =
      await requestStore.createSolicitudFromEspecificacionesTecnicasHTTP(especificacionesTecnicas);

    if (!codigoSolicitud) {
      throw new Error('No se pudo crear la solicitud.');
    }
    useMessageSuccess().open(`Solicitud creada exitosamente!`);
    router.push(`/page/requests/edit/${codigoSolicitud}`);
  } catch (err) {
    useMessageError().open(`Ha ocurrido un error al intentar crear la solicitud.`);
  } finally {
    useLoading().closeLoading();
  }
};

const handleSelectionChange = (selection) => {
  selectedRows.value = selection;
};

const handleGenerateOPFromList = (row) => {
  if (!canCreateWorkOrder) return;
  if (!row?.Codigo) return;
  if (!isApprovedRow(row)) return;
  genOpCodigoET.value = row.Codigo;
  genOpCodigoElementoTipo.value = row?.CodigoElementoTipo?.Codigo ?? null;
  genOpCodigoUnidadVenta.value = row?.CodigoUnidadVenta ?? null;
  modalGenOpVisible.value = true;
};

const confirmGenerateOp = async ({ codigoET, cantidad, codigoUnidadVenta }) => {
  if (!canCreateWorkOrder) return;
  try {
    useLoading().openLoading();

    const op = await createFromETHTTP(codigoET, {
      Cantidad: cantidad,
      CodigoUnidadVenta: codigoUnidadVenta,
    });

    if (!op?.Codigo) throw new Error('No se devolvió el código de OP');

    modalGenOpVisible.value = false;
    useMessageSuccess().open(`OP generada exitosamente!`);
    router.push(`/page/work-orders/edit/${op.Codigo}`);
  } catch (e) {
    console.error(e);
    useMessageError().open('Ha ocurrido un error al intentar generar la OP desde la ET.');
  } finally {
    useLoading().closeLoading();
  }
};

onBeforeMount(async () => {
  resetFilter();
  emptyList();
  firstEspecificacion();
  elementoTipoStore.resetElementoTipoList();
});
</script>

<style scoped>
.version-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 16px;
  padding: 2px 10px 2px 2px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.version-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-radius: 50%;
  height: 22px;
  min-width: 22px;
  padding: 0 4px;
  margin-right: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  color: #303133;
}

.badge-success {
  background-color: #f0f9eb;
  color: #67c638;
  border: 1px solid #e1f3d8;
}
.badge-warning {
  background-color: #fcfdec;
  color: #b88230;
  border: 1px solid #faecd8;
}
.badge-info {
  background-color: #f4f4f5;
  color: #73767a;
  border: 1px solid #e9e9eb;
}
.badge-danger {
  background-color: #fef0f0;
  color: #d34949;
  border: 1px solid #fde2e2;
}
</style>
